<template>
  <section class="flex align-middle justify-center sm:py-40">
    <div class="flex flex-col">
      <a class="link--primary mb-16" :href="redirectTo">
        <div class="inline-block md:pl-0 mt-4"><BackIcon /></div>
        <p class="inline-block ml-8">Pricing</p>
      </a>
      <Card class="w-full max-w-448 p-24">
        <form novalidate @submit.prevent="submit()">
          <h1 class="text-h1 mb-16">Request an upgrade</h1>
          <fieldset>
            <legend class="pb-16 text-body text-grey-body">
              Check if your company’s benefits include unlimited access to all of Sittercity’s features
            </legend>
            <CorporateClientComboBox
              :preselectedId="preselectedId"
              :errorText="errorText"
              :status="employerStatus"
              @selected-client="selectedClient"
              @employer-input-changed="clearEmployerError"
            />
            <text-field
              id="workEmail"
              v-model="workEmail"
              labelName="Work email"
              :helperText="emailFieldHelperText"
              name="workEmail"
              type="email"
              autocomplete="email"
              class="my-16"
              :status="workEmailStatus"
            />
          </fieldset>
          <Button class="w-full" variant="primary" value="Continue" size="medium" type="submit" :loading="isLoading" />
        </form>
      </Card>
      <popup id="errorPopup" v-model="showPopup" popupHeader="We couldn’t send your request" @close="closePopup">
        <p class="text-body mb-16">
          There was an error submitting your request. Try again and if the error continues,
          <a
            href="https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=5652055814427"
            class="text-grey-clickable underline"
            >contact Customer Success</a
          >
        </p>
        <Button
          id="tryAgain"
          variant="primary"
          value="Try again"
          size="medium"
          class="w-full"
          :loading="isLoading"
          @click="closePopup"
        />
      </popup>
    </div>
  </section>
</template>

<script lang="ts">
import Card from '@/components/card/Card.vue';
import TextField from '@/components/textfield/TextField.vue';
import Button from '@/components/button/Button.vue';
import Popup from '@/components/popup/Popup.vue';
import BackIcon from '@/icons/BackIcon.vue';
import helperStatus from '../../../../helpers/status';
import { validateEmail } from '../../../../helpers/emailHelper';
import { ADD_EMPLOYER, GENERATE_CORPORATE_VERIFICATION_TOKEN } from '@/graphql/bh_benefits/mutations';
import { sendSegmentEvent } from '@/lib/segment';
import { isOn } from '@/lib/growthbook';
import CorporateClientComboBox from '../../components/CorporateClientComboBox.vue';

const UPGRADE_SOURCE = 'premium_upgrade';

export default {
  name: 'RequestUpgrade',
  components: {
    Card,
    BackIcon,
    TextField,
    Button,
    Popup,
    CorporateClientComboBox,
  },
  props: {
    redirectTo: { type: String, default: '' },
    preselectedCompany: { type: Object, default: () => {} },
    corporateClients: {
      type: Array,
      default: () => [],
    },
  },
  data(): any {
    return {
      showPopup: false,
      employerNameInput: '',
      selectedCompanySlug: '',
      employerStatus: helperStatus.Neutral,
      workEmail: '',
      workEmailStatus: helperStatus.Neutral,
      isLoading: false,
    };
  },
  computed: {
    automatedCorporateUpgradesEnabled(): boolean {
      return isOn('sc-automate-corporate-upgrades-enabled');
    },
    preselectedId(): string {
      return this.preselectedCompany?.bhClientId || '';
    },
    errorText(): string {
      return 'Enter a valid name';
    },
    emailFieldHelperText() {
      if (this.workEmailStatus === helperStatus.Error) {
        if (this.workEmail.length) {
          return 'Enter a valid email';
        }
        return 'Work email is required';
      }
      return 'Enter the email you use at work';
    },
  },
  watch: {
    workEmail(value) {
      if (this.workEmailStatus === helperStatus.Error) {
        if (validateEmail(value)) {
          this.workEmailStatus = helperStatus.Neutral;
        }
      }
    },
  },
  methods: {
    clearEmployerError() {
      if (this.employerStatus === helperStatus.Error) {
        this.employerStatus = helperStatus.Neutral;
      }
    },
    selectedClient({ slug, name }) {
      this.employerNameInput = name;
      this.selectedCompanySlug = slug;
      if (slug) {
        this.employerStatus = helperStatus.Neutral;
      }
    },
    validateWorkEmail(): boolean {
      const valid = validateEmail(this.workEmail);

      if (!valid) {
        this.workEmailStatus = helperStatus.Error;
      }

      return valid;
    },
    validateEmployerSelection(): boolean {
      const valid = this.selectedCompanySlug.length > 0;

      if (!valid) {
        this.employerStatus = helperStatus.Error;
      }

      return valid;
    },
    async submit() {
      const emailValid = this.validateWorkEmail();
      const employerValid = this.validateEmployerSelection();

      if (emailValid && employerValid) {
        await this.addEmployerMutation();
      }
    },
    async submitLegacyEmployerMutation() {
      await this.$apollo.mutate({
        mutation: ADD_EMPLOYER,
        variables: {
          workEmail: this.workEmail,
          employerSlug: this.selectedCompanySlug,
          employerName: this.employerNameInput,
          source: UPGRADE_SOURCE,
        },
      });
    },
    async generateCorporateVerificationToken(): Promise<boolean> {
      try {
        await this.$apollo.mutate({
          mutation: GENERATE_CORPORATE_VERIFICATION_TOKEN,
          variables: {
            workEmail: this.workEmail,
            employerSlug: this.selectedCompanySlug,
          },
        });
        return true;
      } catch (_error) {
        return false;
      }
    },
    async addEmployerMutation() {
      this.isLoading = true;
      await this.$nextTick();
      let upgradePath = 'manual';

      try {
        if (this.automatedCorporateUpgradesEnabled) {
          if (await this.generateCorporateVerificationToken()) {
            upgradePath = 'verification';
          } else {
            await this.submitLegacyEmployerMutation();
            upgradePath = 'manual_fallback';
          }
        } else {
          await this.submitLegacyEmployerMutation();
        }

        sendSegmentEvent('CorporateBenefit_RequestUpgrade', {
          employerSlugEntered: this.selectedCompanySlug,
          employerNameInput: this.employerNameInput,
          source: UPGRADE_SOURCE,
          upgradePath,
        });

        this.$emit('advanceStep', {
          upgradePath,
          ...(upgradePath === 'verification' && { workEmail: this.workEmail }),
        });
      } catch (_error) {
        this.showPopup = true;
      } finally {
        this.isLoading = false;
      }
    },
    closePopup() {
      this.showPopup = false;
    },
  },
};
</script>
